import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLangSync } from './context/LanguageContext';
import { MarketingCopyProvider } from './context/MarketingCopyContext';
import { localizedPath } from './utils/routes';
import Navbar from './components/Navbar';
import LocalBusinessJsonLd from './components/LocalBusinessJsonLd';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Gallery from './pages/Gallery';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import BookAppointment from './pages/BookAppointment';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import EmailVerification from './components/EmailVerification';
import SetPassword from './pages/SetPassword';
import ForgotPassword from './pages/ForgotPassword';
import PaymentResult from './pages/PaymentResult';

// Scroll to top on route change and page load
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AuthenticatedRoute({ children }) {
  const { user, loading } = useAuth();
  const { lang } = useParams();
  if (loading) return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh',color:'#D42B2B',fontFamily:'Rajdhani',fontSize:'1.5rem',letterSpacing:'0.08em'}}>LOADING...</div>;
  return user ? children : <Navigate to={localizedPath('login', lang || 'fr')} />;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const { lang } = useParams();
  if (loading) return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh',color:'#D42B2B',fontFamily:'Rajdhani',fontSize:'1.5rem',letterSpacing:'0.08em'}}>LOADING...</div>;
  if (!user) return <Navigate to={localizedPath('login', lang || 'fr')} />;
  if (!user.email_verified && user.role !== 'admin') return <Navigate to={localizedPath('verify-email', lang || 'fr')} />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const { lang } = useParams();
  if (loading) return null;
  return user?.role === 'admin' ? children : <Navigate to={`/${lang || 'fr'}`} />;
}

// Component that syncs the URL lang param with LanguageContext
function LangSync({ children }) {
  useLangSync();
  return children;
}

function LocalizedRoutes() {
  return (
    <LangSync>
      <Navbar />
      <Routes>
        {/* Shared routes (same slug in FR and EN) */}
        <Route index element={<Home />} />
        <Route path="services" element={<Services />} />
        <Route path="contact" element={<Contact />} />
        <Route path="faq" element={<FAQ />} />
        <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

        {/* French-specific routes */}
        <Route path="a-propos" element={<About />} />
        <Route path="galerie" element={<Gallery />} />
        <Route path="confidentialite" element={<Privacy />} />
        <Route path="conditions" element={<Terms />} />
        <Route path="connexion" element={<Login />} />
        <Route path="mot-de-passe-oublie" element={<ForgotPassword />} />
        <Route path="inscription" element={<Register />} />
        <Route path="verification-courriel" element={<AuthenticatedRoute><EmailVerification /></AuthenticatedRoute>} />
        <Route path="rendez-vous" element={<PrivateRoute><BookAppointment /></PrivateRoute>} />
        <Route path="rendez-vous/payment-success" element={<PrivateRoute><PaymentResult type="success" /></PrivateRoute>} />
        <Route path="rendez-vous/payment-cancel" element={<PrivateRoute><PaymentResult type="cancel" /></PrivateRoute>} />
        <Route path="tableau-de-bord" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="definir-mot-de-passe" element={<SetPassword />} />

        {/* English-specific routes */}
        <Route path="about" element={<About />} />
        <Route path="gallery" element={<Gallery />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms" element={<Terms />} />
        <Route path="book" element={<PrivateRoute><BookAppointment /></PrivateRoute>} />
        <Route path="book/payment-success" element={<PrivateRoute><PaymentResult type="success" /></PrivateRoute>} />
        <Route path="book/payment-cancel" element={<PrivateRoute><PaymentResult type="cancel" /></PrivateRoute>} />
        <Route path="login" element={<Login />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="register" element={<Register />} />
        <Route path="verify-email" element={<AuthenticatedRoute><EmailVerification /></AuthenticatedRoute>} />
        <Route path="dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="set-password" element={<SetPassword />} />
      </Routes>
      <Footer />
    </LangSync>
  );
}

// Redirect that preserves query string and hash
function RedirectWithQuery({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <LocalBusinessJsonLd />
      <Routes>
        {/* Root redirects to /fr */}
        <Route path="/" element={<Navigate to="/fr" replace />} />

        {/* Old bare path redirects to French localized paths (preserving query params) */}
        <Route path="/services" element={<RedirectWithQuery to="/fr/services" />} />
        <Route path="/about" element={<RedirectWithQuery to="/fr/a-propos" />} />
        <Route path="/contact" element={<RedirectWithQuery to="/fr/contact" />} />
        <Route path="/faq" element={<RedirectWithQuery to="/fr/faq" />} />
        <Route path="/gallery" element={<RedirectWithQuery to="/fr/galerie" />} />
        <Route path="/book" element={<RedirectWithQuery to="/fr/rendez-vous" />} />
        <Route path="/privacy" element={<RedirectWithQuery to="/fr/confidentialite" />} />
        <Route path="/terms" element={<RedirectWithQuery to="/fr/conditions" />} />
        <Route path="/login" element={<RedirectWithQuery to="/fr/connexion" />} />
        <Route path="/register" element={<RedirectWithQuery to="/fr/inscription" />} />
        <Route path="/dashboard" element={<RedirectWithQuery to="/fr/tableau-de-bord" />} />
        <Route path="/admin" element={<RedirectWithQuery to="/fr/admin" />} />
        <Route path="/verify-email" element={<RedirectWithQuery to="/fr/verification-courriel" />} />
        <Route path="/set-password" element={<RedirectWithQuery to="/fr/definir-mot-de-passe" />} />
        <Route path="/forgot-password" element={<RedirectWithQuery to="/fr/mot-de-passe-oublie" />} />

        {/* Localized routes under /:lang */}
        <Route path="/:lang/*" element={<LocalizedRoutes />} />

        {/* Catch-all: redirect to /fr */}
        <Route path="*" element={<Navigate to="/fr" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  useEffect(() => {
    const splash = document.getElementById('splash-screen');
    if (splash) {
      splash.style.opacity = '0';
      setTimeout(() => splash.remove(), 400);
    }
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <LanguageProvider>
          <MarketingCopyProvider>
            <AppRoutes />
          </MarketingCopyProvider>
        </LanguageProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
